import { gql, useMutation, useQuery } from '@apollo/client'
import PageComponent from 'components/Dashboard/PageComponent'
import { Form, Formik } from 'formik'
import { Link } from 'gatsby'
import { orderBy } from 'lodash'
import HtmlContent from '../HtmlContent'
import { YesNoInput } from '../RadioInput'
import SubmitButton from '../SubmitButton'
import TextInput from '../TextInput'
import { DashboardTitle } from './DashboardTitleContext'
import { AddThirdParty, AddThirdPartyVariables } from './__generated__/AddThirdParty'
import { AllThirdParties } from './__generated__/AllThirdParties'

const thirdPartyFragment = gql`
  fragment ThirdPartyDisplayProps on ThirdParty {
    id
    status
    displayName
    consentRequired
    registrantNoteHtml
    resultsEmail
    createdAt
    createdBy
  }
`

const allThirdPartiesQuery = gql`
  query AllThirdParties {
    allThirdParties {
      ...ThirdPartyDisplayProps
    }
  }

  ${thirdPartyFragment}
`

const addThirdPartyMutation = gql`
  mutation AddThirdParty($input: AddThirdPartyInput!) {
    addThirdParty(input: $input) {
      success
      thirdParty {
        ...ThirdPartyDisplayProps
      }
    }
  }

  ${thirdPartyFragment}
`

const ThirdParties: PageComponent = () => {
  const { loading, error, data } = useQuery<AllThirdParties>(allThirdPartiesQuery)
  const [addThirdParty] = useMutation<AddThirdParty, AddThirdPartyVariables>(addThirdPartyMutation)

  if (error) {
    return <p>ERROR {JSON.stringify(error, null, 2)}</p>
  }

  return (
    <div className="container">
      <DashboardTitle title="Third Parties" />

      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">All Third Parties</p>
            {loading && <p className="has-text-info ml-3">Loading...</p>}
          </div>
        </div>
      </nav>

      {data && (
        <table className="table is-bordered is-fullwidth" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Display Name</th>
              <th>Consent required?</th>

              <th>Results email</th>
            </tr>
          </thead>
          <tbody>
            {orderBy(data.allThirdParties, (x) => x.displayName).map((thirdParty) => (
              <tr key={thirdParty.id}>
                <td>
                  <Link to={`/dashboard/third-party/${thirdParty.id}`}>{thirdParty.id}</Link>
                </td>
                <td>{thirdParty.displayName}</td>
                <td>
                  {thirdParty.consentRequired ? 'Yes' : 'No'}

                  {thirdParty.registrantNoteHtml && (
                    <>
                      <br />
                      <strong>Note for registrant: </strong>
                      &ldquo;
                      <HtmlContent rawHtml={thirdParty.registrantNoteHtml} />
                      &rdquo;
                    </>
                  )}
                </td>
                <td>{thirdParty.resultsEmail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="mt-4 subtitle is-5">New Third Party</p>
      <Formik
        initialValues={{
          id: '',
          displayName: '',
          resultsEmail: '',
          consentRequired: 'Yes',
          registrantNoteHtml: '',
        }}
        onSubmit={async (values) => {
          const result = await addThirdParty({
            variables: {
              input: {
                ...values,
                consentRequired: values.consentRequired === 'Yes',
              },
            },
          })

          console.log('RESULT', result)
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <TextInput name="id" label="ID" />
            <TextInput name="displayName" label="Display Name" />
            <TextInput name="resultsEmail" label="Results Email" />
            <TextInput name="registrantNoteHtml" label="Note for registrant (optional, html allowed)" />
            <YesNoInput name="consentRequired">
              Consent required (i.e. consent text will be shown on reg form)
            </YesNoInput>

            <div className="field" style={{ paddingTop: 18 }}>
              <SubmitButton className="is-midnightBlue is-rounded" submitting={isSubmitting}>
                Create new 3rd party
              </SubmitButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

ThirdParties.displayName = 'Third Parties'
ThirdParties.path = '/third-parties'

export default ThirdParties
